import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { logger } from './logger';
import type { Currency } from '../stores/meetingStore';
import { formatCurrency } from './formatters';
import { i18nStore } from '../stores/i18nStore';

/**
 * Check if speech synthesis is supported
 */
export function isSpeechSynthesisSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return 'speechSynthesis' in window;
}

/**
 * Get translated string from i18n with fallback
 */
function getTranslation(key: string): string {
  try {
    // Check if i18n is initialized
    if (typeof get === 'function' && typeof _ !== 'undefined') {
      const translated = get(_)(key);
      // If translation returns the key itself, it means translation wasn't found
      // In that case, use fallback
      if (translated && translated !== key) {
        return translated;
      }
    }
  } catch {
    // Fall through to fallback
  }
  
  // Fallback translations (English)
  const fallbacks: Record<string, string> = {
    'notifications.yellowThreshold.body': 'Cost has reached the yellow threshold: {cost}',
    'notifications.redThreshold.body': 'Cost has reached the red threshold: {cost}',
  };
  
  return fallbacks[key] || key;
}

/**
 * Get language code for speech synthesis based on i18n store
 */
function getSpeechLanguage(): string {
  try {
    const currentLanguage = get(i18nStore);
    // Map i18n language codes to speech synthesis language codes
    const languageMap: Record<string, string> = {
      'en': 'en-US',
      'de': 'de-DE',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ja': 'ja-JP',
    };
    return languageMap[currentLanguage] || 'en-US';
  } catch {
    return 'en-US';
  }
}

/**
 * Speak a threshold warning using Web Speech API
 * @param thresholdType 'yellow' or 'red'
 * @param cost Current meeting cost
 * @param currency Currency code
 */
export async function speakThresholdWarning(
  thresholdType: 'yellow' | 'red',
  cost: number,
  currency: Currency
): Promise<void> {
  if (!isSpeechSynthesisSupported()) {
    logger.warn('Speech synthesis is not supported');
    return;
  }

  try {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Get current language for formatting
    const currentLanguage = get(i18nStore);
    
    // Format the cost
    const formattedCost = formatCurrency(cost, currency, currentLanguage);
    
    // Get localized string
    const bodyKey = `notifications.${thresholdType}Threshold.body`;
    let text = getTranslation(bodyKey);
    
    // Replace {cost} placeholder
    text = text.replace('{cost}', formattedCost);

    // Create speech utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = getSpeechLanguage();
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Speak the text
    window.speechSynthesis.speak(utterance);

    logger.debug('Speech synthesis triggered', { thresholdType, cost, currency, text, lang: utterance.lang });
  } catch (error) {
    logger.error('Error speaking threshold warning', error);
  }
}
